import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { PhoneNumberValues } from 'core/application/adminSignUp/common/models'

import InputMask from 'common/components/dataEntry/formik/FormikInputMaskField'
import Alert from 'common/components/feedback/Alert'
import Icon from 'common/components/general/Icon'
import CategoryItemPageHeader from 'common/components/onBoarding/Common'
import { phoneNumberRegex, phoneNumberRegexWithUnderscore } from 'common/components/utils/InputUtils'

import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import useCommandRequest from '../../common/hooks/useCommandRequest'
import WizardNextButton from './WizardNextButton'
import WizardPreviousButton from './WizardPreviousButton'

import './CompanyDetailsStyles.less'

interface PhoneNumberPros extends BaseNavigationProps<PhoneNumberValues> {
  onSubmitFormHandler: (values: PhoneNumberValues) => Promise<any>
  message: string
  description: string
  numberOfQuestions: number
  currentQuestion: number
}

const PhoneNumber: React.FC<PhoneNumberPros> = (props) => {
  const { t: translate } = useTranslation()
  const [sendCommand, isLoading] = useCommandRequest()

  const onSubmit = async (values: PhoneNumberValues) => {
    const valuesToSubmit = {
      ...values,
      optionalPhoneNumber: values.optionalPhoneNumber ? values.optionalPhoneNumber?.split('_').join('') : '',
    }

    const result = await sendCommand(() => props.onSubmitFormHandler(valuesToSubmit))

    if (result.completed) {
      props.goNext(props.name)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" className={'t-admin-signup-phone-number'}>
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 8 }}>
              <CategoryItemPageHeader
                title={translate('admin-signup.company-details.title')}
                numberOfQuestions={props.numberOfQuestions}
                currentQuestion={props.currentQuestion}
                question={translate('admin-signup.company-details.phone-number.question')}
              />
            </Col>
          </Row>
          <Row className="form__row mt-32">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <div className="pl-12">
                <Alert
                  type="info"
                  className={'t-admin-signup-phone-number-info'}
                  showIcon
                  message={props.message}
                  description={props.description}
                />
              </div>
            </Col>
          </Row>
          <Row className="form__row mt-24">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <InputMask
                label={translate('admin-signup.company-details.phone-number.main-phone-number-label')}
                name="mainPhoneNumber"
                mask="(000) 000-0000"
                placeholder="(555) 123-4567"
                className="formik_input  phone_number"
                suffix={<Icon type="fa fa-phone" className="gray-6" />}
              />
            </Col>
          </Row>
          <Row className="form__row">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <InputMask
                label={translate('admin-signup.company-details.phone-number.optional-phone-number-label')}
                name="optionalPhoneNumber"
                mask="(000) 000-0000"
                placeholder="(555) 123-4567"
                hint="(Optional)"
                className="formik_input phone_number"
                suffix={<Icon type="fa fa-phone" className="gray-6" />}
                onChange={(e) => {
                  if (e.unmaskedValue === '') {
                    formik.setFieldValue('optionalPhoneNumber', null)
                  } else {
                    formik.setFieldValue('optionalPhoneNumber', e.maskedValue)
                  }
                }}
              />
            </Col>
          </Row>
          <Row className="bottom__actions">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 3 }}>
              <WizardPreviousButton
                onClick={() => {
                  props.goBack(props.name)
                }}
              />
              <WizardNextButton disabled={!formValidator(translate).isValidSync(formik.values)} loading={isLoading} />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default PhoneNumber

const formValidator = (translate: any) =>
  Yup.object({
    mainPhoneNumber: Yup.string()
      .required(translate('general.required'))
      .matches(phoneNumberRegex, translate('admin-signup.company-details.errors.phone-number-regex')),

    optionalPhoneNumber: Yup.string()
      .nullable()
      .notRequired()
      .trim()
      .transform((value) => (value && value.length > 0 ? value : null))
      .matches(phoneNumberRegex, translate('admin-signup.company-details.errors.phone-number-regex')),
  })
